// ** React Imports
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

// ** MUI Imports
import { styled } from '@mui/material/styles'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'

// ** Icons Imports
import Sync from 'mdi-material-ui/Sync'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchPricingbooksData,
  pricingBooksSelector,
  editPricingBook,
  pushPricingBookToSalesforce,
  cleanMessages
} from 'store/metaData/pricingbook'
import { productsSelector, getProductsData } from 'store/products'
import { currencySelector, fetchCurrencyData } from 'store/metaData/currency'

// ** 3rd Party Libraries
import moment from 'moment'
import { isEqual } from 'lodash'

// ** Custom Components
import FallbackSpinner from 'core/components/spinner'
import AlertMessages from 'pages/Billing/components/AlertMessages'
import { useAuth } from 'hooks/useAuth'

// Styled Card component
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  border: `solid 1px ${theme.palette.grey[300]}`
}))

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedCheckboxIcon = <CheckBoxIcon fontSize='small' />

const SalesforceSyncDetail = () => {
  // ** Hooks
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useAuth()

  // ** Selectors
  const { pricingBooksData, success, error, loading } = useSelector(pricingBooksSelector)
  const { products } = useSelector(productsSelector)
  const { currenciesData } = useSelector(currencySelector)

  // ** State
  const [prices, setPrices] = useState([])
  const [originalPrices, setOriginalPrices] = useState([])
  const [initialized, setInitialized] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [productsToAdd, setProductsToAdd] = useState([])

  const pricingBook = useMemo(
    () => pricingBooksData?.find(item => item.id?.toString() === id?.toString()),
    [pricingBooksData, id]
  )

  const isDirty = !isEqual(prices, originalPrices)
  const isBusy = loading === 'UPDATE' || loading === 'PUSH_TO_SALESFORCE'

  // ** Fetch data
  useEffect(() => {
    if (!pricingBooksData || pricingBooksData.length < 1) {
      dispatch(fetchPricingbooksData()).then(() => setHasFetched(true))
    } else {
      setHasFetched(true)
    }
    dispatch(getProductsData({ limit: 1000, page: 1, field: '' }))
    dispatch(fetchCurrencyData())
  }, [])

  // ** Reset local edit state whenever we navigate to a different pricing book
  useEffect(() => {
    setInitialized(false)
    setPrices([])
    setOriginalPrices([])
  }, [id])

  // ** Seed local price state once the pricing book is available
  useEffect(() => {
    if (pricingBook && !initialized) {
      setPrices(pricingBook.prices ?? [])
      setOriginalPrices(pricingBook.prices ?? [])
      setInitialized(true)
    }
  }, [pricingBook, initialized])

  // ** After a successful update, re-sync local state from the saved data so Push re-enables
  useEffect(() => {
    if (success === 'UPDATE' && pricingBook) {
      setPrices(pricingBook.prices ?? [])
      setOriginalPrices(pricingBook.prices ?? [])
    }
  }, [success])

  // ** Warn on accidental tab close / refresh while there are unsaved edits
  useEffect(() => {
    const handler = e => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // ** Distinct type/currency columns actually used by this pricing book
  const columns = useMemo(() => {
    const seen = new Set()
    const cols = []
    prices?.forEach(price => {
      const key = `${price.type}-${price.currencyId}`
      if (!seen.has(key)) {
        seen.add(key)
        const currency = currenciesData?.find(item => item.id === price.currencyId)
        cols.push({
          type: price.type,
          currencyId: price.currencyId,
          label: `${price.type === 'annual' ? 'COMMITMENT' : price.type?.toUpperCase()}-${currency?.iso_code || ''}`
        })
      }
    })
    // ** Sort deterministically so column order doesn't shuffle based on API response order
    return cols.sort((a, b) => a.label.localeCompare(b.label))
  }, [prices, currenciesData])

  // ** One row per distinct product referenced by this pricing book's prices.
  // ** Newly added (unsaved) products are listed first, in the order they were added;
  // ** already-saved products follow, sorted alphabetically for a stable order across updates.
  const rows = useMemo(() => {
    const productIds = [...new Set(prices?.map(price => price.productId))]
    const originalProductIds = new Set(originalPrices?.map(price => price.productId))

    const rowsData = productIds.map(productId => ({
      productId,
      product: products?.find(item => item.id === productId),
      isNew: !originalProductIds.has(productId)
    }))

    const newRows = rowsData.filter(row => row.isNew)
    const existingRows = rowsData
      .filter(row => !row.isNew)
      .sort((a, b) => {
        const nameA = a.product?.skuName || ''
        const nameB = b.product?.skuName || ''
        return nameA.localeCompare(nameB) || String(a.productId).localeCompare(String(b.productId))
      })

    return [...newRows, ...existingRows]
  }, [prices, originalPrices, products])

  // ** Products not yet part of this pricing book, available to add
  const availableProductsToAdd = useMemo(() => {
    const existingProductIds = new Set(rows.map(row => row.productId))
    return products?.filter(product => !existingProductIds.has(product.id)) || []
  }, [products, rows])

  const addProductsHandler = () => {
    if (productsToAdd.length < 1 || columns.length < 1) return
    setPrices(state => [
      ...state,
      ...productsToAdd.flatMap(product =>
        columns.map(col => ({ productId: product.id, type: col.type, currencyId: col.currencyId, value: 0 }))
      )
    ])
    setProductsToAdd([])
  }

  const priceFor = (productId, column) =>
    prices?.find(price => price.productId === productId && price.type === column.type && price.currencyId === column.currencyId)

  const changePrice = (productId, column, rawValue) => {
    const value = parseFloat(rawValue)
    if (isNaN(value) || value < 0) return
    setPrices(state => {
      const exists = state.some(
        price => price.productId === productId && price.type === column.type && price.currencyId === column.currencyId
      )
      return exists
        ? state.map(price =>
            price.productId === productId && price.type === column.type && price.currencyId === column.currencyId
              ? { ...price, value }
              : price
          )
        : [...state, { productId, type: column.type, currencyId: column.currencyId, value }]
    })
  }

  const discardChangesHandler = () => setPrices(originalPrices)

  const updateHandler = () => {
    if (!pricingBook || isBusy) return
    const editedPrices = prices.filter(price => {
      const original = originalPrices.find(
        item => item.productId === price.productId && item.type === price.type && item.currencyId === price.currencyId
      )
      return !original || original.value !== price.value
    })
    if (editedPrices.length < 1) return
    const body = {
      id: pricingBook.id,
      name: pricingBook.name,
      from: moment(pricingBook.from).format('YYYY-MM-DD'),
      to: moment(pricingBook.to).format('YYYY-MM-DD'),
      type: 'pricing_book',
      status: pricingBook.status,
      is_default: pricingBook.is_default,
      prices: editedPrices
    }
    const log = {
      customer_id: id,
      date: new Date(),
      user: user?.email,
      model: 'pricingBook',
      instance_name: 'prices',
      old_values: { pricingBook },
      new_values: { pricingBook: body }
    }
    dispatch(editPricingBook(body, log, { skipDefaultSync: true }))
  }

  const pushHandler = () => {
    if (isDirty || isBusy) return
    dispatch(pushPricingBookToSalesforce(id))
  }

  const backHandler = () => {
    if (isDirty && !window.confirm('You have unsaved price changes. Leave without saving?')) return
    navigate('/pricing-books/salesforce-sync')
  }

  const resetMessagesHandler = () => dispatch(cleanMessages())

  if (!pricingBook) {
    if (!hasFetched || loading === 'GET') return <FallbackSpinner />

    return (
      <StyledCard sx={{ p: 10, textAlign: 'center' }}>
        <Typography sx={{ mb: 4 }}>
          {error === 'GET' ? 'Failed to load pricing book data.' : 'Pricing book not found.'}
        </Typography>
        <Button variant='text' onClick={() => navigate('/pricing-books/salesforce-sync')}>
          Back to list
        </Button>
      </StyledCard>
    )
  }

  return (
    <StyledCard sx={{ position: 'relative' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='h6'>{pricingBook.name}</Typography>
            {pricingBook.is_default && (
              <Chip
                label='Default'
                size='small'
                sx={{ backgroundColor: 'common.blue', color: 'common.white', fontWeight: 600 }}
              />
            )}
          </Box>
        }
        subheader={`${pricingBook.from ? moment(pricingBook.from).format('MMM DD, YYYY') : '------'} - ${
          pricingBook.to ? moment(pricingBook.to).format('MMM DD, YYYY') : '------'
        } • ${pricingBook.status || '------'}`}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Button size='medium' variant='text' onClick={backHandler}>
              Back
            </Button>
            {isDirty && (
              <Button size='medium' variant='text' onClick={discardChangesHandler} disabled={isBusy}>
                Discard changes
              </Button>
            )}
            <Button
              size='medium'
              variant='text'
              sx={{ fontWeight: 600 }}
              onClick={updateHandler}
              disabled={!isDirty || isBusy}
              startIcon={loading === 'UPDATE' && <CircularProgress size='1rem' />}
            >
              Update
            </Button>
            <Button
              size='medium'
              variant='contained'
              sx={{ fontWeight: 600 }}
              onClick={pushHandler}
              disabled={isDirty || isBusy}
              startIcon={<Sync />}
              endIcon={loading === 'PUSH_TO_SALESFORCE' && <CircularProgress size='1rem' sx={{ color: 'common.white' }} />}
            >
              Push to Salesforce
            </Button>
          </Box>
        }
      />
      {isDirty && (
        <Box sx={{ px: 4, pb: 2 }}>
          <Typography variant='caption' sx={{ color: 'warning.main' }}>
            You have unsaved price changes. Save them before pushing to Salesforce.
          </Typography>
        </Box>
      )}
      <Box sx={{ px: 4, pb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          size='small'
          sx={{ width: 420, '.MuiAutocomplete-tag': { background: '#00000014' } }}
          options={availableProductsToAdd}
          getOptionLabel={option => option?.skuName || ''}
          value={productsToAdd}
          onChange={(event, value) => setProductsToAdd(value)}
          disabled={!initialized || columns.length < 1 || isBusy}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox icon={checkboxIcon} checkedIcon={checkedCheckboxIcon} style={{ marginRight: 8 }} checked={selected} />
              {option.skuName} ({option.skuId})
            </li>
          )}
          renderInput={params => <TextField {...params} label='Add products' placeholder='Search by product name' />}
        />
        <Button
          size='medium'
          variant='text'
          sx={{ fontWeight: 600 }}
          onClick={addProductsHandler}
          disabled={!initialized || productsToAdd.length < 1 || columns.length < 1 || isBusy}
        >
          Add {productsToAdd.length > 1 ? `${productsToAdd.length} products` : 'product'}
        </Button>
        {!initialized ? (
          <CircularProgress size='1rem' />
        ) : (
          columns.length < 1 && (
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              This pricing book has no prices yet — add its first price from the main Pricing Books page.
            </Typography>
          )
        )}
      </Box>
      <Box sx={{ px: 4, pb: 4 }}>
        <TableContainer component={Paper}>
          <Table aria-label='pricing book prices'>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                {columns.map(col => (
                  <TableCell key={`${col.type}-${col.currencyId}`} align='left'>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!initialized ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center'>
                    <CircularProgress size='1.5rem' />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align='center'>
                    No prices configured for this pricing book yet.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(({ productId, product }) => (
                  <TableRow key={productId}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{product?.skuName || productId}</TableCell>
                    {columns.map(col => {
                      const price = priceFor(productId, col)

                      return (
                        <TableCell key={`${col.type}-${col.currencyId}`}>
                          <TextField
                            variant='outlined'
                            size='small'
                            type='number'
                            value={price?.value ?? 0}
                            onChange={e => changePrice(productId, col, e.target.value)}
                            disabled={isBusy}
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <AlertMessages
        success={success}
        error={error}
        messageValue={success === 'UPDATE' ? 'UPDATE' : 'PUSH_TO_SALESFORCE'}
        message={
          (success === 'UPDATE' && 'Prices were updated successfully !') ||
          (success === 'PUSH_TO_SALESFORCE' && 'Pricing book was pushed to Salesforce successfully !') ||
          (error === 'PUSH_TO_SALESFORCE' && 'Failed to push pricing book to Salesforce. Please try again.') ||
          (error === 'UPDATE' && 'Failed to update prices. Please try again.') ||
          (error === 'GET' && 'Failed to load pricing book data. Please try again.')
        }
        resetHandler={resetMessagesHandler}
      />
    </StyledCard>
  )
}

export default SalesforceSyncDetail
